/**
 * El teclado de la pantalla, solo cuando se toca la casilla.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ (lo pidió el dueño, 2026-09-30)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Un cliente de TOTAL prestó un teléfono Android con lector de códigos
 * incluido, y los hay que compran un lector Bluetooth para el teléfono. Los dos
 * «escriben» el código en la casilla que tiene el cursor, como en la
 * computadora. Para que el lector esté siempre listo, Vender y la entrada de
 * mercancía le devuelven el cursor a su buscador después de cada producto.
 *
 * En la computadora eso no molesta. En el teléfono, cada vez que el buscador
 * recibe el cursor sale el teclado de la pantalla y tapa medio catálogo, y
 * escanear pierde la fluidez que tiene en la computadora.
 *
 * Con esto el buscador se queda con el cursor SIN teclado (`inputMode="none"`:
 * el lector sigue escribiendo, porque no es el teclado de la pantalla), y el
 * teclado sale solo cuando la persona toca la casilla para escribir un nombre.
 * Al salir de la casilla, vuelve a esperar al lector.
 *
 * En la computadora no cambia nada: `inputMode` solo lo mira el teclado de la
 * pantalla.
 */

import { useRef, useState, type RefObject } from "react";

export function useTecladoADemanda(ref: RefObject<HTMLInputElement | null>) {
  const [conTeclado, setConTeclado] = useState(false);
  // El `blur` que provocamos nosotros para volver a abrir la casilla con
  // teclado no cuenta como «salió de la casilla».
  const ignorarBlur = useRef(false);

  return {
    inputMode: (conTeclado ? "search" : "none") as "search" | "none",
    onPointerDown: () => {
      if (conTeclado) return;
      setConTeclado(true);
      const casilla = ref.current;
      // Si ya tenía el cursor (se lo dio la pantalla, sin teclado), se le
      // quita un instante: el toque se lo devuelve, esta vez con teclado.
      if (casilla && document.activeElement === casilla) {
        ignorarBlur.current = true;
        casilla.blur();
      }
    },
    onBlur: () => {
      if (ignorarBlur.current) {
        ignorarBlur.current = false;
        return;
      }
      setConTeclado(false);
    },
    /** Después de leer un código: el teclado se guarda y se espera al lector. */
    guardarTeclado: () => setConTeclado(false),
  };
}
